import { z } from 'zod'

import { NotFoundError } from '../lib/errors'

export const REGION_IDS = { UK: 'uk', US: 'us' } as const

export type RegionId = (typeof REGION_IDS)[keyof typeof REGION_IDS]

/** Locale configuration for a region. Currency amounts are integers in the smallest unit. */
export interface Region {
  id: RegionId
  /** Display name, e.g. "United Kingdom". */
  name: string
  /** Flag emoji for compact display in region pickers. */
  flag: string
  currency: 'GBP' | 'USD'
  currencySymbol: '£' | '$'
  /** BCP-47 locale for currency/number formatting. */
  locale: 'en-GB' | 'en-US'
  /**
   * IANA timezone the region's wall-clock times are displayed in. Records with
   * no clinic of their own (notes, vitals, treatments, files) are rendered in
   * this zone; clinic-bound records use the clinic's own timezone, which is
   * more precise if a region ever spans several zones.
   */
  timezone: string
  /** Smallest-unit label, display only. */
  minorUnit: 'pence' | 'cents'
  weightUnit: 'kg' | 'lb'
  temperatureUnit: 'C' | 'F'
  /** Whether times display in 12-hour (AM/PM) format rather than 24-hour. */
  hour12: boolean
  /** Whether prices include tax (UK VAT) or tax is added on top (US sales tax). */
  taxMode: 'inclusive' | 'exclusive'
  /** UI label for the staff professional-registration field. */
  staffRegistrationLabel: string
  /**
   * Whether checkout offers tap-to-pay. Taps are taken in the Stripe Dashboard app of the
   * region's account, so this is per region rather than per clinic.
   */
  tapToPay: boolean
  /**
   * How a vaccine's booster reminder target is chosen. `'disease-list'` (US) offers a
   * fixed per-species disease dropdown, since one vaccine can cover several diseases.
   * `'product-name'` (UK) uses the vaccine's product name as the target, as a UK vaccine
   * maps 1:1 to a disease — so no dropdown is shown.
   */
  vaccineTargetMode: 'disease-list' | 'product-name'
  /** Country whose numbering plan reads local-format phone numbers (no `+` prefix). */
  phoneCountry: 'GB' | 'US'
  /** UI label for the postal code / ZIP field in address forms. */
  postalCodeLabel: 'Postcode' | 'ZIP code'
  /** Root URL of the region's marketing website. */
  marketingWebsiteUrl: string
  /** Pricing page on the marketing website. */
  pricingUrl: string
  /**
   * Fee charged for a missed appointment, in the smallest currency unit. See
   * `formatClinicAppointmentPenaltyFee` for how it is rendered into client-facing copy.
   */
  noShowFee: number
}

export const REGIONS_BY_ID = {
  [REGION_IDS.UK]: {
    id: REGION_IDS.UK,
    name: 'United Kingdom',
    flag: '🇬🇧',
    currency: 'GBP',
    currencySymbol: '£',
    locale: 'en-GB',
    timezone: 'Europe/London',
    minorUnit: 'pence',
    weightUnit: 'kg',
    temperatureUnit: 'C',
    hour12: false,
    taxMode: 'inclusive',
    staffRegistrationLabel: 'RCVS number',
    tapToPay: true,
    vaccineTargetMode: 'product-name',
    phoneCountry: 'GB',
    postalCodeLabel: 'Postcode',
    marketingWebsiteUrl: 'https://www.snootsvet.com',
    pricingUrl: 'https://www.snootsvet.com/#pricing',
    noShowFee: 2500,
  },
  [REGION_IDS.US]: {
    id: REGION_IDS.US,
    name: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    currencySymbol: '$',
    locale: 'en-US',
    timezone: 'America/New_York',
    minorUnit: 'cents',
    weightUnit: 'lb',
    temperatureUnit: 'F',
    hour12: true,
    taxMode: 'exclusive',
    staffRegistrationLabel: 'NJ license number',
    tapToPay: false,
    vaccineTargetMode: 'disease-list',
    phoneCountry: 'US',
    postalCodeLabel: 'ZIP code',
    marketingWebsiteUrl: 'https://www.joinsnoots.com/us',
    pricingUrl: 'https://www.joinsnoots.com/us#pricing',
    noShowFee: 6500,
  },
} satisfies Record<RegionId, Region>

/** Zod enum of region ids, for validating query params / bodies. */
export const regionIdSchema = z.enum(Object.values(REGION_IDS))

export const DEFAULT_TIMEZONE = 'Europe/London'
export const DEFAULT_LOCALE = 'en-GB'

/**
 * BCP-47 locale to use for each supported currency, derived from the region
 * config so region locale/currency pairings stay the single source of truth.
 * (e.g. GBP → en-GB, USD → en-US, so USD renders "$" rather than the en-GB "US$".)
 */
const CURRENCY_LOCALES: Record<string, string> = Object.fromEntries(
  Object.values(REGIONS_BY_ID).map((r) => [r.currency, r.locale])
)

/**
 * Formats a smallest-unit amount into a human-readable currency string
 * (e.g. 1428, "gbp" → "£14.28"; 1428, "usd" → "$14.28").
 *
 * The locale is chosen from the currency so the symbol renders in its own
 * convention (e.g. USD as "$", not the en-GB "US$"). Accepts Stripe-style
 * lowercase codes as well as uppercase. Unknown currencies fall back to en-GB.
 */
export function formatCurrency(amount: number, currency: string): string {
  const locale = CURRENCY_LOCALES[currency.toUpperCase()] ?? DEFAULT_LOCALE
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount / 100)
}

/** Returns the region with the given slug, or null if blank/unknown. */
export function getRegion(id?: string | null): Region | null {
  if (!id) return null
  return REGIONS_BY_ID[id as RegionId] ?? null
}

/** Returns the region with the given slug, or throws NotFoundError if unknown. */
export function requireRegion(id: string): Region {
  const region = REGIONS_BY_ID[id as RegionId]
  if (!region) throw new NotFoundError(`Region '${id}' not found`)
  return region
}

/**
 * Formats a smallest-unit amount (pence/cents) as a localized currency string for a region.
 * e.g. (1428, 'uk') -> "£14.28", (1428, 'us') -> "$14.28".
 */
export function formatRegionPrice(minorUnits: number, regionId: string): string {
  const region = requireRegion(regionId)
  return new Intl.NumberFormat(region.locale, {
    style: 'currency',
    currency: region.currency,
  }).format(minorUnits / 100)
}

const LB_PER_KG = 2.20462

/** Converts kilograms to pounds. */
export function kgToLb(kg: number): number {
  return kg * LB_PER_KG
}

/** Converts pounds to kilograms. */
export function lbToKg(lb: number): number {
  return lb / LB_PER_KG
}

/** Converts degrees Celsius to degrees Fahrenheit. */
export function celsiusToFahrenheit(c: number): number {
  return (c * 9) / 5 + 32
}

/** Converts degrees Fahrenheit to degrees Celsius. */
export function fahrenheitToCelsius(f: number): number {
  return ((f - 32) * 5) / 9
}

/** A weight broken into display parts. Weight is always kg-primary; `alt` carries pounds where shown. */
export interface WeightDisplay {
  /** Primary value, always in kg (the canonical entry/storage unit). */
  value: string
  /** Primary unit label, always "kg". */
  unit: string
  /** Secondary value in the alternate unit (e.g. "27.6 lb"), or null for regions that don't show it. */
  alt: string | null
}

/**
 * Breaks a canonical kg weight into display parts for a region, so slot-based
 * surfaces (stat cards, chips) can style the value, unit, and secondary unit
 * independently. Weight is always kg-primary; the US surfaces pounds as `alt`.
 * UK -> { value: "12.5", unit: "kg", alt: null }
 * US -> { value: "12.5", unit: "kg", alt: "27.6 lb" }
 */
export function weightDisplay(kg: number, regionId: RegionId): WeightDisplay {
  const region = requireRegion(regionId)
  return {
    value: String(kg),
    unit: 'kg',
    alt: region.weightUnit === 'lb' ? `${kgToLb(kg).toFixed(1)} lb` : null,
  }
}

/** A temperature broken into its converted value and unit label for a region. */
export interface TemperatureDisplay {
  /** Value converted to the region's temperature unit. */
  value: string
  /** Unit label, "°F" or "°C". */
  unit: string
}

/**
 * Breaks a canonical °C temperature into display parts for a region, so slot-based
 * surfaces can style the value and unit independently. US converts to °F.
 * UK -> { value: "39.1", unit: "°C" }
 * US -> { value: "102.4", unit: "°F" }
 */
export function temperatureDisplay(celsius: number, regionId: RegionId): TemperatureDisplay {
  const region = requireRegion(regionId)
  if (region.temperatureUnit === 'F') {
    return { value: celsiusToFahrenheit(celsius).toFixed(1), unit: '°F' }
  }
  return { value: String(celsius), unit: '°C' }
}

/**
 * Formats a canonical kg weight as a single string for flowing-text / print
 * surfaces. UK shows kg only; US shows kg primary with pounds alongside,
 * e.g. "12.5 kg (27.6 lb)". For slot-based surfaces use {@link weightDisplay}.
 */
export function formatWeight(kg: number, regionId: RegionId): string {
  const { value, unit, alt } = weightDisplay(kg, regionId)
  return alt ? `${value} ${unit} (${alt})` : `${value} ${unit}`
}

/**
 * Formats a canonical °C temperature as a single string for flowing-text / print
 * surfaces. UK shows °C; US shows °F only, e.g. "102.4 °F". For slot-based
 * surfaces use {@link temperatureDisplay}.
 */
export function formatTemperature(celsius: number, regionId: RegionId): string {
  const { value, unit } = temperatureDisplay(celsius, regionId)
  return `${value} ${unit}`
}
